import React from 'react'
import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material'
import { endOfDay, formatISO, isValid as isValidDate, parse, parseISO, startOfDay } from '@e3dc-react/shell/Libs/date'
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import useModals from 'src/Hooks/useModals'
import { logWarn } from 'src/Libs/flow/utils'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { DatePicker, TextField } from 'src/Libs/inputs/react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useDialogActions } from '../hooks'
import { EditBadgeDialogProps } from '../types'
import { BadgePOSTorPUTDto, useCreateBadge, useDeleteBadge, useUpdateBadge } from 'src/Libs/flow/end-user-settings'
import { RFID_UNLIMITED_EXPIRATION_DATE, RfidCardPreview } from '../../../charging-stations'
import { asciiPattern } from '../../../validations'
import { useAppRatingContext } from 'src/Libs/app-rating/hooks'

//#region Dialog

export const EditBadgeDialog: React.FC<EditBadgeDialogProps> = ({ initialBadge, installationId, onClose, place }) => {
  const { t } = useCustomTranslation()
  const { triggerAppRating } = useAppRatingContext()

  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))

  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()

  const { openConfirmationModal } = useModals()

  const validationSchema = useValidationSchema()

  const methods = useForm<BadgeFormValues>({
    defaultValues: {
      badgeName: initialBadge?.name ?? defaultBadgeFormValues.badgeName,
      badgeId: initialBadge?.badgeId ?? defaultBadgeFormValues.badgeId,
      expirationDate: getInitialDate(initialBadge?.expirationDate, defaultBadgeFormValues.expirationDate),
    },
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
  })

  const { handleSubmit, control, formState } = methods
  const { isValid } = formState

  const [badgeName, badgeId, expirationDate] = useWatch({
    control,
    name: ['badgeName', 'badgeId', 'expirationDate'],
  })

  const existingBadge = !!initialBadge

  const { mutate: createRfid, isPending: isCreatePending } = useCreateBadge(installationId)
  const { mutate: updateRfid, isPending: isUpdatePending } = useUpdateBadge(installationId)
  const { mutate: deleteRfid, isPending: isDeletePending } = useDeleteBadge(installationId)

  const isUpsertPending = isCreatePending || isUpdatePending

  const handleSuccess = (translationKey: string, itemName: string): void => {
    enqueueSuccessStack(t(translationKey, '', { item: itemName }))
    handleOnClose()
  }

  const handleError = (error: Error, translationKey: string, itemName?: string): void => {
    logWarn(error)
    enqueueErrorStack(itemName ? `${t(translationKey, '', { item: itemName })}` : t(translationKey))
  }

  const upsertRfid = (badge: BadgeFormValues): void => {
    const newBadge: BadgePOSTorPUTDto = {
      badgeId: badge.badgeId,
      name: badge.badgeName,
      expirationDate: parseBadgeExpirationDate(badge.expirationDate),
    }

    if (existingBadge) {
      updateRfid(
        { badgeId: initialBadge.badgeId, badge: newBadge },
        {
          onSuccess: () => handleSuccess('common.postSuccess', badge.badgeName),
          onError: error => handleError(error, 'common.postFailed', badge.badgeName),
        }
      )
    } else {
      createRfid(newBadge, {
        onSuccess: () => handleSuccess('common.postSuccess', badge.badgeName),
        onError: error => handleError(error, 'common.postFailed', badge.badgeName),
      })
    }
  }

  const handleDeleteBadge = (): void => {
    if (!existingBadge) return

    deleteRfid(initialBadge.badgeId, {
      onSuccess: () => handleSuccess('common.deleteSuccess', initialBadge?.name ?? ''),
      onError: error => handleError(error, 'common.deleteFailed'),
    })
  }

  const onSubmit: SubmitHandler<BadgeFormValues> = badge => {
    upsertRfid(badge)
    triggerAppRating()
  }

  const onDeleteBadge = (): void => {
    openConfirmationModal({
      title: t('installation.rfidCardEditor.deleteTitle', '', {
        name: initialBadge?.name,
      }),
      text: t('installation.rfidCardEditor.deleteMessage'),
      buttonText: [t('common.cancel'), t('common.confirm')],
      onConfirm: () => {
        handleDeleteBadge()
        triggerAppRating()
      },
    })
  }

  const handleOnClose = (): void => {
    onClose?.()
  }

  const isDataMutating = isUpsertPending || isDeletePending
  const isPrimaryActionDisabled = isDataMutating || !isValid
  const isSecondaryDisabled = isDataMutating
  const isAdditionalDisabled = isDataMutating || !existingBadge
  const dialogActions = useDialogActions({
    primary: {
      name: t('common.confirm'),
      onClick: handleSubmit(onSubmit),
      disabled: isPrimaryActionDisabled,
      isLoading: isUpsertPending,
    },
    secondary: {
      disabled: isSecondaryDisabled,
      onClick: handleOnClose,
    },
    additional: {
      onClick: onDeleteBadge,
      disabled: isAdditionalDisabled,
      isLoading: isDeletePending,
    },
  })

  return (
    <FormProvider {...methods}>
      <Dialog title={t('installation.rfidCardEditor.title')} onClose={handleOnClose} maxWidth={'md'} actions={dialogActions}>
        <Box component={'form'} display={'contents'} onSubmit={handleSubmit(onSubmit)}>
          <Box pt={3} px={downSm ? undefined : 3}>
            <Grid container>
              <Grid item xs={12} md={6}>
                <RfidCardPreview
                  name={badgeName}
                  badgeId={badgeId}
                  expirationDate={expirationDate && isValidDate(expirationDate) ? formatISO(expirationDate) : undefined}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="h3">{t('installation.rfidCardEditor.badgeName')}</Typography>
                  <TextField
                    name="badgeName"
                    required
                    label={t('installation.rfidCardEditor.badgeNameField')}
                    fullWidth
                    value={badgeName}
                    maxLength={20}
                  />
                  <TextField
                    name="badgeId"
                    required
                    label={t('installation.rfidCardEditor.badgeId')}
                    fullWidth
                    value={badgeId}
                    minLength={4}
                    maxLength={32}
                  />
                  <DatePicker
                    name={'expirationDate'}
                    label={t('installation.rfidCardEditor.endDate')}
                    onTransform={date => (date ? date.toDate() : null)}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Dialog>
    </FormProvider>
  )
}
//#endregion

//#region validation
const useValidationSchema = (): z.ZodSchema<BadgeFormValues> => {
  const { t } = useCustomTranslation()

  const validationSchema = z.object({
    badgeName: z
      .string()
      .min(1)
      .max(20)
      .refine(() => asciiPattern(t)),
    badgeId: z
      .string()
      .min(4)
      .max(32)
      .refine(val => /^[A-Fa-f0-9]+$/.test(val)),
    expirationDate: z
      .date()
      .nullable()
      .refine(val => val === null || (isValidDate(val) && getIsDateWithinDatePickerRange(val)), 'Invalid date'),
  })

  return validationSchema
}
//#endregion

//#region Helpers
const parseBadgeExpirationDate = (expirationDate: Date | null): string => {
  if (!expirationDate) return RFID_UNLIMITED_EXPIRATION_DATE

  const endOfDayDate = endOfDay(expirationDate)
  return formatISO(endOfDayDate)
}

type BadgeFormValues = {
  badgeName: string
  badgeId: string
  expirationDate: Date | null
}

const defaultBadgeFormValues: BadgeFormValues = {
  badgeName: '',
  badgeId: '',
  expirationDate: null,
}

const getInitialDate = (date: string | undefined, fallback: Date | null): Date | null => {
  if (!date) return fallback
  const parsedDate = parseISO(date)
  if (!isValidDate(parsedDate)) return null
  if (!getIsDateWithinDatePickerRange(parsedDate)) return null
  return parsedDate
}

/** Default min date for <DatePicker /> */
const MIN_DATE = parse('01/01/1900', 'dd/MM/yyyy', new Date())
/** Default max date for <DatePicker /> */
const MAX_DATE = parse('31/12/2099', 'dd/MM/yyyy', new Date())

const getIsDateWithinDatePickerRange = (date: Date): boolean => {
  const minDateStart = startOfDay(MIN_DATE)
  const maxDateStart = startOfDay(MAX_DATE)
  const dateStart = startOfDay(date)
  return minDateStart <= dateStart && dateStart <= maxDateStart
}
//#endregion
